import { useState, useEffect } from 'react';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import Badge from './Shared/Badge';
import { exportToCSV } from '../utils/csvExport';
import { today } from '../utils/formatters';
import {
  fetchSanitationLogs, insertSanitationLog,
  updateSanitationLog, deleteSanitationLog,
} from '../services/sanitationService';

const METHODS      = ['70% Alcohol', 'Barbicide', 'UV Sterilizer', 'Autoclave', 'Other'];
const TOOL_PRESETS = ['Nail File','Buffer','Cuticle Nipper','Cuticle Pusher','Gel Brush','Dust Brush','Tweezers','Drill Bit','Spatula','Tip Cutter'];

export default function SanitationLog() {
  const [logs,       setLogs]       = useState([]);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState({ date: today(), tools: [], customTool: '', methods: [], duration: '10', notes: '' });
  const [filterDate, setFilterDate] = useState('');
  const [loadAll,    setLoadAll]    = useState(false);

  useEffect(() => { fetchLogs(); }, [loadAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    const data = await fetchSanitationLogs(loadAll);
    setLogs(data);
  };

  const filteredLogs = filterDate ? logs.filter(l => l.date === filterDate) : logs;

  const handleExport = () => {
    if (filteredLogs.length === 0) return alert('No records to export!');
    exportToCSV('Sanitation_Log', ['Date','Methods','Tools','Duration (mins)','Notes'],
      filteredLogs.map(l => [l.date, l.methods?.join(' | ') || '', l.tools?.join(' | ') || '', l.duration, l.notes]));
  };

  const saveLog = async () => {
    const allTools = [...form.tools, ...(form.customTool.trim() ? [form.customTool.trim()] : [])];
    if (!allTools.length)    return alert('Please select or enter at least one tool!');
    if (!form.methods.length) return alert('Please select at least one method!');
    const payload = { date: form.date, methods: form.methods, tools: allTools, duration: parseInt(form.duration) || 0, notes: form.notes };
    try {
      if (editId) await updateSanitationLog(editId, payload);
      else        await insertSanitationLog(payload);
      fetchLogs(); cancelForm();
    } catch (err) { alert('Error saving record: ' + err.message); }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    await deleteSanitationLog(id);
    fetchLogs();
  };

  const startEdit = (log) => {
    setEditId(log.id);
    setForm({ date: log.date, tools: log.tools.filter(t => TOOL_PRESETS.includes(t)), customTool: log.tools.filter(t => !TOOL_PRESETS.includes(t)).join(', '), methods: log.methods || [], duration: String(log.duration), notes: log.notes || '' });
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditId(null); setForm({ date: today(), tools: [], customTool: '', methods: [], duration: '10', notes: '' }); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>Sanitation Log</h2>
          <p style={{ fontSize: '13px', color: P.muted }}>
            {filteredLogs.length}{filterDate ? ' filtered' : ''} / {logs.length} records loaded
            {!loadAll && logs.length === 100 && <span> · <button onClick={() => setLoadAll(true)} style={{ background: 'none', border: 'none', color: P.rose, cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: 0 }}>Load all</button></span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={handleExport} variant="secondary" size="sm">📊 Export to Sheets</Btn>
          <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? 'ghost' : 'primary'} size="sm">{showForm ? 'Cancel' : '+ New Record'}</Btn>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
        <input type="date" style={{ ...inputStyle, width: '160px' }} value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        {filterDate && <Btn size="sm" variant="ghost" onClick={() => setFilterDate('')}>✕ Clear</Btn>}
      </div>
      {showForm && (
        <div style={{ ...cardStyle, border: `2px solid ${P.rose}88` }}>
          <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Methods *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
              {METHODS.map(m => <button key={m} onClick={() => setForm({ ...form, methods: form.methods.includes(m) ? form.methods.filter(x => x !== m) : [...form.methods, m] })} style={{ padding: '6px 13px', borderRadius: '20px', border: `1.5px solid ${form.methods.includes(m) ? P.rose : P.border}`, background: form.methods.includes(m) ? P.rose + '18' : 'transparent', color: form.methods.includes(m) ? P.rose : P.muted, cursor: 'pointer' }}>{m}</button>)}
            </div>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Tools *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '10px' }}>
              {TOOL_PRESETS.map(t => <button key={t} onClick={() => setForm({ ...form, tools: form.tools.includes(t) ? form.tools.filter(x => x !== t) : [...form.tools, t] })} style={{ padding: '6px 13px', borderRadius: '20px', border: `1.5px solid ${P.border}`, background: form.tools.includes(t) ? P.rose + '18' : 'transparent', color: form.tools.includes(t) ? P.rose : P.muted, cursor: 'pointer' }}>{t}</button>)}
            </div>
            <input style={inputStyle} value={form.customTool} onChange={e => setForm({ ...form, customTool: e.target.value })} placeholder="Custom tools (Optional)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
            <div><label style={labelStyle}>Duration (mins)</label><input type="number" style={inputStyle} value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} /></div>
            <div><label style={labelStyle}>Notes</label><input style={inputStyle} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <Btn onClick={saveLog} style={{ width: '100%' }}>Save Record</Btn>
        </div>
      )}
      <div style={{ maxHeight: '520px', overflowY: 'auto', paddingRight: '2px' }}>
        {filteredLogs.map(log => (
          <div key={log.id} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}><span style={{ fontWeight: 700 }}>{log.date}</span>{log.methods?.map(m => <Badge key={m}>{m}</Badge>)}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{log.tools.map(t => <span key={t} style={{ background: P.peach, fontSize: '12px', padding: '2px 8px', borderRadius: '10px' }}>{t}</span>)}</div>
                {log.notes && <p style={{ fontSize: '13px', color: P.muted, marginTop: '6px' }}>📝 {log.notes}</p>}
              </div>
              <div>
                <button onClick={() => startEdit(log)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✏️</button>
                <button onClick={() => deleteLog(log.id)} style={{ background: 'none', border: 'none', color: P.red, cursor: 'pointer', marginLeft: '6px' }}>×</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
